/**
 * 비밀번호 재설정 요청 페이지
 * 
 * @description
 * 사용자가 이메일을 입력하여 비밀번호 재설정 요청을 보내는 페이지입니다.
 * 
 * @features
 * - 이메일 입력 및 재설정 요청
 * - 재설정 메일 발송 완료 안내
 * - AlertDialog 통합 (성공/실패 메시지)
 * 
 * @api
 * - POST /auth/password-reset
 * 
 * @routes
 * - /password-reset (현재 페이지)
 * - /login (로그인 페이지)
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Input, AlertDialog } from '@/components/shared/ui';
import { ROUTES } from '@/lib/constants/routes';
import { useAuth } from '@/data/queries/useAuth';

export default function PasswordResetRequest(): React.ReactElement {
	const { t } = useTranslation('auth');
	// ========== API Hooks ==========
	const { requestPasswordReset } = useAuth();
	
	// ========== State 관리 ==========
	
	/**
	 * 이메일 입력값
	 */
	const [email, setEmail] = useState<string>('');
	
	/**
	 * 비밀번호 재설정 요청 진행 중 상태
	 * @description API 호출 중 버튼 비활성화
	 */
	const [isLoading, setIsLoading] = useState<boolean>(false);
	
	/**
	 * AlertDialog 상태
	 * @field isOpen - 팝업 열림 여부
	 * @field message - 표시할 메시지
	 */
	const [alertState, setAlertState] = useState<{ isOpen: boolean; message: string; }>({
		isOpen: false,
		message: '',
	});

	// ========== Hooks ==========
	
	const navigate = useNavigate();

	// ========== Helper 함수 ==========
	
	/**
	 * AlertDialog 표시
	 * @param message - 표시할 메시지
	 * @description AlertDialog를 열고 메시지를 설정
	 */
	const showAlert = (message: string): void => {
		setAlertState({
			isOpen: true,
			message,
		});
	};

	/**
	 * AlertDialog 닫기
	 * @description AlertDialog를 닫음
	 */
	const closeAlert = (): void => {
		setAlertState({
			isOpen: false,
			message: '',
		});
	};

	// ========== Event Handlers ==========
	
	/**
	 * 폼 제출 핸들러
	 * @param e - 폼 제출 이벤트
	 * @description 이메일 입력 후 비밀번호 재설정 요청 처리
	 * 
	 * @process
	 * 1. 이메일 입력 여부 확인
	 * 2. 이메일 형식 확인
	 * 3. API 호출
	 * 4. 새 비밀번호 입력 페이지로 이동
	 */
	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
		e.preventDefault();

		// 이메일 입력 여부 확인
		if (!email.trim()) {
			showAlert(t('passwordReset.request.alertEmailRequired'));
			return;
		}

		// 이메일 형식 확인 (간단한 검증)
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(email.trim())) {
			showAlert(t('passwordReset.request.alertEmailInvalid'));
			return;
		}

		try {
			setIsLoading(true);

		await requestPasswordReset(email.trim());
		
		// 성공 메시지 표시
		showAlert(t('passwordReset.request.alertSuccess'));
			
			// 1.5초 후 로그인 페이지로 이동
			// 참고: 실제 API에서는 resetToken이 이메일로 전송되며,
			// 사용자는 이메일의 링크를 클릭하여 비밀번호 재설정 페이지로 이동합니다.
			setTimeout(() => {
				navigate(ROUTES.AUTH.LOGIN);
			}, 2000);
		} catch (error: any) {
			showAlert(error.message || t('passwordReset.request.alertError'));
		} finally {
			// 항상 실행: 로딩 상태 종료
			setIsLoading(false);
		}
	};

	return (
		<div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6">
			<div className="w-full max-w-[460px] bg-white rounded-3xl p-10 pb-14 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.08)] border border-gray-100">
				{/* Header */}
				<div className="text-center mb-10">
					<div className="mb-12 flex justify-center items-center">
					<Link to={ROUTES.HOME} className="inline-block">
						<img src="/assets/images/brand/medicrew_blue_logo.png" alt="Medicrew" className="h-7 object-contain mx-auto" />
					</Link>
					</div>
					<h1 className="text-xl font-bold text-brand-500 mb-2">
						{t('passwordReset.request.pageTitle')}
					</h1>
					<div className="text-gray-600 text-sm leading-relaxed mb-6">
						<p>{t('passwordReset.request.description')}</p>
					</div>
				</div>

				{/* Form */}
				<form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
					<div>
						<label htmlFor="email" className="block mb-3 text-sm font-semibold text-gray-900">
							{t('email')}
						</label>
						<Input
							type="email"
							id="email"
							className="border rounded-xl border-gray-200"
							placeholder="example@email.com"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							disabled={isLoading}
						/>
					</div>

				<div className="flex justify-center items-center gap-4">
					<Button
						type="submit"
						className="px-6 min-w-40 rounded-full text-base active:scale-95 h-10"
						variant="primary"
						disabled={isLoading}
					>
						{isLoading ? t('passwordReset.request.sending') : t('passwordReset.request.submitButton')}
					</Button>
					<Button
						type="button"
						className="px-6 min-w-40 rounded-full text-base active:scale-95 h-10"
						variant="secondary"
						onClick={() => navigate(-1)}
						disabled={isLoading}
					>
						{t('passwordReset.request.cancel')}
					</Button>
				</div>
				</form>

				{/* Help Section */}
				<div className="mt-14">
					<h3 className="text-sm font-bold text-gray-800 mb-2">
						{t('passwordReset.request.helpTitle')}
					</h3>
					<div className="text-sm text-gray-500 p-0 leading-relaxed">
						<p><span>- {t('passwordReset.request.noteDelay')}</span></p>
						<p><span>- {t('passwordReset.request.noteSpam')}</span></p>
						<p><span>- {t('passwordReset.request.noteFirewall')}</span></p>
					</div>
				</div>
			</div>
		{/* Powered By */}
		<div className="mt-8 flex justify-center items-center gap-2">
				<span className="text-xs text-gray-800">Powered by</span>
				<span className="text-sm font-bold text-gray-800">NEWBASE</span>
			</div>

			{/* AlertDialog */}
			<AlertDialog
				isOpen={alertState.isOpen}
				onClose={closeAlert}
				message={alertState.message}
			/>
		</div>
	);
}
